import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useState } from 'react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import QrScanner from './QrScanner.jsx';
import { extractCode } from '../lib/joinCode.js';

const JOIN_CLINIC = `
  mutation JoinClinicByCode($invitationCode: String!) {
    joinClinicByCode(invitationCode: $invitationCode) {
      id
      status
      clinic { id name clinicType province city address officePhone invitationCode }
    }
  }
`;

const inputBase = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
};

/**
 * Join a clinic by invitation code — typed, or scanned from the clinic's QR.
 *
 * Self-contained so it can be mounted both in onboarding and from the doctor's
 * own panel. `onJoined(link)` fires once the link exists; the caller decides what
 * to do next (advance the wizard, refresh a list, …).
 *
 * The mutation is idempotent server-side: re-joining a clinic the doctor is
 * already linked to returns the existing link rather than adding them twice.
 */
export default function JoinClinicPanel({ onJoined, showTitle = true }) {
  const { t } = useTranslation();
  const [code, setCode]         = useState('');
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const join = async (raw) => {
    const invitationCode = String(raw ?? code).trim().toUpperCase();
    if (!invitationCode) { setError(t('validation.clinic.joinCodeRequired')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await bffMutation(JOIN_CLINIC, { invitationCode });
      const link = res?.joinClinicByCode;
      if (!link?.id) throw new Error(t('errors.clinic.join'));
      onJoined?.(link);
    } catch (e) {
      if (e.message?.includes('CLINIC_NOT_FOUND') || e.message?.includes(t('clinic.joinCode'))) {
        setError(t('errors.clinic.invalidCode'));
      } else {
        setError(t('errors.clinic.join'));
      }
    } finally {
      setLoading(false);
    }
  };

  // The clinic's QR holds a URL; extractCode pulls the invitation code out of it.
  const handleDetected = (rawValue) => {
    const extracted = extractCode(rawValue);
    setScanning(false);
    setCode(extracted);
    join(extracted);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {showTitle && (
        <div >
          <div style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, marginBottom: 4 }}>
            {t('clinic.join')}
          </div>
          <div style={{ fontSize: 12, color: C.textSecondary }}>
            {t('clinic.joinPrompt')}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={code}
          onChange={e => { setCode(e.target.value.toUpperCase()); setError(null); }}
          placeholder={t('clinic.joinCodeExample')}
          maxLength={16}
          disabled={loading}
          style={{
            ...inputBase, flex: 1, direction: 'ltr', textAlign: 'center',
            fontSize: 18, fontWeight: 700, letterSpacing: 4,
          }}
          onFocus={e => { e.target.style.borderColor = C.navy; }}
          onBlur={e => { e.target.style.borderColor = C.border; }}
          onKeyDown={e => { if (e.key === 'Enter') join(); }}
        />
        <button
          type="button"
          onClick={() => setScanning(s => !s)}
          title={scanning ? t('clinic.stopScan') : t('clinic.scanQr')}
          style={{
            width: 40, height: 40, borderRadius: 8, flexShrink: 0,
            background: scanning ? '#FEF2F2' : '#f8fafc',
            border: `1.5px solid ${scanning ? '#FECACA' : C.border}`,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {scanning ? (
            <svg viewBox="0 0 24 24" fill={C.red} width="18" height="18"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill={C.textSecondary} width="18" height="18"><path d="M9.5 6.5v3h-3v-3h3M11 5H5v6h6V5zm-1.5 9.5v3h-3v-3h3M11 13H5v6h6v-6zm6.5-6.5v3h-3v-3h3M19 5h-6v6h6V5zm-6 8h1.5v1.5H13V13zm1.5 1.5H16V16h-1.5v-1.5zM16 13h1.5v1.5H16V13zm-3 3h1.5v1.5H13V16zm1.5 1.5H16V19h-1.5v-1.5zM16 16h1.5v1.5H16V16zm1.5-1.5H19V16h-1.5v-1.5zm0 3H19V19h-1.5v-1.5zM22 7h-2V4h-3V2h5v5zm0 15v-5h-2v3h-3v2h5zM2 22h5v-2H4v-3H2v5zM2 2v5h2V4h3V2H2z"/></svg>
          )}
        </button>
      </div>

      {scanning && (
        <>
          <QrScanner onDetected={handleDetected} onClose={() => setScanning(false)} height={220} />
          <div style={{ fontSize: 12, color: C.textSecondary, textAlign: 'center' }}>
            {t('clinic.qrFramePrompt')}
          </div>
        </>
      )}

      {error && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
          padding: '10px 14px', fontSize: 12, color: '#B91C1C', 
        }}>
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={() => join()}
        disabled={loading || !code.trim()}
        style={{
          padding: '11px 16px', borderRadius: 8, border: 'none',
          background: C.navy, color: '#fff', fontSize: 14, fontWeight: 700,
          cursor: loading || !code.trim() ? 'not-allowed' : 'pointer',
          opacity: loading || !code.trim() ? 0.5 : 1,
        }}
      >
        {loading ? t('clinic.joining') : t('clinic.join')}
      </button>
    </div>
  );
}
